import { Check, Sparkles, TrendingDown, Ban } from "lucide-react";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { SiTether, SiEthereum } from "react-icons/si";
import { useSiteT } from "../../hooks/useSiteT";
import { tierLabel } from "./VolumeTiersSection";
import { VOLUME_TIERS } from "../../data/fees";

function pct(n) {
    return `${Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}

export default function PricingSection() {
    const { t } = useSiteT();
    const starter = VOLUME_TIERS[0];
    const features = ["pr.f1", "pr.f2", "pr.f3", "pr.f4", "pr.f5", "pr.f6"].map(t);
    const cols = [t("pr.colVolume"), "White-hat", "Gray-hat", "Casino & Gambling"];

    return (
        <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 relative">
            <div className="container-skala">
                <div className="max-w-3xl mb-14 space-y-4">
                    <div className="inline-flex items-center gap-2"><span className="h-px w-8 bg-highlight" /><p className="text-[11px] font-mono uppercase tracking-[0.3em] text-highlight">{t("pr.eyebrow")}</p></div>
                    <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight">
                        {t("pr.h1")} <span className="serif-accent text-foreground/85">{t("pr.h2")}</span>
                    </h2>
                    <p className="text-muted-foreground text-base md:text-lg">{t("pr.sub")}</p>
                    <div className="flex items-center justify-center gap-4 pt-2 text-muted-foreground">
                        <span className="text-[10px] font-mono uppercase tracking-[0.25em]">Top-up</span>
                        <SiTether className="w-4 h-4" />
                        <SiEthereum className="w-4 h-4" />
                    </div>
                </div>

                <div className="grid lg:grid-cols-[1fr_1.3fr] gap-5 md:gap-6 max-w-6xl mx-auto items-stretch">
                    {/* Single offer card */}
                    <article data-testid="pricing-tier-no-subscription" className="relative rounded-3xl p-8 md:p-10 border border-primary/50 bg-card glow-primary flex flex-col">
                        <div className="absolute -top-3 left-8 px-4 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-[0.25em] uppercase inline-flex items-center gap-1.5">
                            <Ban className="w-3 h-3" /> {t("pr.noSub")}
                        </div>
                        <div className="space-y-1 mb-6">
                            <h3 className="font-display font-bold text-2xl">{t("pr.cardTitle")}</h3>
                            <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{t("pr.cardSub")}</p>
                        </div>
                        <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-display font-black text-6xl md:text-7xl" data-testid="pricing-starter-fee">{pct(starter.whiteHat)}</span>
                            <span className="text-muted-foreground text-sm">{t("pr.from")}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-7">{t("pr.perTopup")} · $0 {t("pr.monthlyFee")}</p>

                        <Link to="/signup" data-testid="pricing-cta-signup">
                            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12">{t("pr.cta")}</Button>
                        </Link>
                        <p className="text-[11px] font-mono text-muted-foreground text-center mt-3">{t("pr.note")}</p>

                        <ul className="mt-7 space-y-3">
                            {features.map((f) => (
                                <li key={f} className="flex items-start gap-3 text-sm">
                                    <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full bg-primary/15 text-primary">
                                        <Check className="w-3 h-3" />
                                    </span>
                                    <span className="text-foreground/85">{f}</span>
                                </li>
                            ))}
                        </ul>
                    </article>

                    {/* Fee ladder */}
                    <article data-testid="pricing-fee-table" className="rounded-3xl border border-white/[0.08] bg-card overflow-hidden flex flex-col">
                        <div className="px-6 md:px-8 py-6 border-b border-white/[0.06] flex items-center justify-between gap-3">
                            <div>
                                <h3 className="font-display font-bold text-2xl">{t("pr.tableTitle")}</h3>
                                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">{t("pr.charged")}</p>
                            </div>
                            <TrendingDown className="w-6 h-6 text-primary shrink-0" />
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                                        {cols.map((col, i) => (
                                            <th key={col} className={`px-4 md:px-6 py-3 font-medium ${i === 0 ? "text-left" : "text-right"}`}>{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/[0.05]">
                                    {VOLUME_TIERS.map((tier) => (
                                        <tr key={tier.id} data-testid={`pricing-tier-row-${tier.id}`} className="hover:bg-white/[0.02] transition-colors">
                                            <td className="px-4 md:px-6 py-4">
                                                <div className="flex items-center gap-2.5">
                                                    <span className="w-2 h-2 rounded-full" style={{ background: tier.accent }} />
                                                    <div>
                                                        <p className="font-semibold" style={{ color: tier.accent }}>{tier.name}</p>
                                                        <p className="text-[11px] text-muted-foreground font-mono">{tierLabel(t, tier.label)}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 md:px-6 py-4 text-right font-display font-bold text-lg text-[#00b67a] tabular-nums">{pct(tier.whiteHat)}</td>
                                            <td className="px-4 md:px-6 py-4 text-right font-display font-bold text-lg tabular-nums">{pct(tier.grayHat)}</td>
                                            <td className="px-4 md:px-6 py-4 text-right font-display font-bold text-lg text-muted-foreground tabular-nums">
                                                {pct(tier.casino)}
                                                {tier.cashback && (
                                                    <span className="block text-[10px] font-mono text-[#2FA57C] font-medium tracking-wide">{t("pr.cashback")}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-6 md:px-8 py-5 mt-auto border-t border-white/[0.06] flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                            <p className="text-xs text-muted-foreground max-w-md">{t("pr.minNote")}</p>
                            <a href="#volume-rewards" data-testid="pricing-volume-link" className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/20 hover:bg-white/[0.08] text-sm font-medium transition-colors">
                                {t("pr.seeLadder")} →
                            </a>
                        </div>
                    </article>
                </div>

                <div className="mt-12 max-w-6xl mx-auto rounded-2xl border border-white/[0.08] bg-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" data-testid="pricing-volume-reminder">
                    <div className="space-y-2">
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary inline-flex items-center gap-2">
                            <Sparkles className="w-3 h-3" /> {t("pr.volEyebrow")}
                        </p>
                        <h4 className="font-display font-semibold text-xl">{t("pr.volTitle")}</h4>
                        <p className="text-sm text-muted-foreground">{t("pr.volSub")}</p>
                    </div>
                    <Link to="/calculator" data-testid="pricing-calculator-link" className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/20 hover:bg-white/[0.08] text-sm font-medium transition-colors">
                        {t("pr.calc")} →
                    </Link>
                </div>

                <p className="text-center text-xs text-muted-foreground mt-10 font-mono tracking-wide">{t("pr.footer")}</p>
            </div>
        </section>
    );
}
